import { memoize } from 'lodash'
import { APIConfig, Id } from 'types/definitions'

import { IndividualProfileServicePromiseClient } from '@taxfyle/api-internal/internal/individual_profile_grpc_web_pb'
import { enableDevTools } from 'utils/grpc-util'
import { createGrpcAuthenticator } from 'infra/grpc-authenticator'
import {
  GetDependentProfileForJobRequest,
  GetDependentProfileRequest,
  GetPersonalProfileForJobRequest,
  GetPersonalProfileRequest,
  GetSpousalProfileForJobRequest,
  GetSpousalProfileRequest,
} from '@taxfyle/api-internal/internal/individual_profile_pb'

export function createIndividualProfileAPI(config: APIConfig) {
  return new IndividualProfileAPI(config)
}

export class IndividualProfileAPI {
  private readonly individualProfileClient = createIndividualProfileClient(
    this.config.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(this.config)

  constructor(private config: APIConfig) {}

  /**
   * Gets the personal profile for a given job ID.
   */
  async getPersonalProfileForJob(jobId: Id) {
    const req = new GetPersonalProfileForJobRequest().setJobId(Id.assert(jobId))

    return this.individualProfileClient
      .getPersonalProfileForJob(req, await this.grpcAuthenticator.getMeta())
      .then((res) => res.toObject())
  }

  /**
   * Gets a personal profile.
   */
  async getPersonalProfile(id: Id) {
    const req = new GetPersonalProfileRequest().setWorkspaceId(Id.assert(id))

    return this.individualProfileClient
      .getPersonalProfile(req, await this.grpcAuthenticator.getMeta())
      .then((res) => res.toObject())
  }

  /**
   * Gets the spousal profile for a given job ID.
   */
  async getSpousalProfileForJob(jobId: Id) {
    const req = new GetSpousalProfileForJobRequest().setJobId(Id.assert(jobId))

    return this.individualProfileClient
      .getSpousalProfileForJob(req, await this.grpcAuthenticator.getMeta())
      .then((res) => res.toObject())
  }

  /**
   * Gets a spousal profile.
   */
  async getSpousalProfile(id: Id) {
    const req = new GetSpousalProfileRequest().setWorkspaceId(Id.assert(id))

    return this.individualProfileClient
      .getSpousalProfile(req, await this.grpcAuthenticator.getMeta())
      .then((res) => res.toObject())
  }

  /**
   * Gets the dependent profile for a given job ID.
   */
  async getDependentProfileForJob(id: Id, jobId: Id) {
    const req = new GetDependentProfileForJobRequest()
      .setId(Id.assert(id))
      .setJobId(Id.assert(jobId))

    return this.individualProfileClient
      .getDependentProfileForJob(req, await this.grpcAuthenticator.getMeta())
      .then((res) => res.toObject())
  }

  /**
   * Gets a dependent's profile.
   */
  async getDependentProfile(id: Id) {
    const req = new GetDependentProfileRequest().setId(Id.assert(id))

    return this.individualProfileClient
      .getDependentProfile(req, await this.grpcAuthenticator.getMeta())
      .then((res) => res.toObject())
  }
}

/**
 * Creates an individual profile client that is memoized.
 */
const createIndividualProfileClient = memoize(
  function creatIndividualProfileGrpcClient(baseUrl: string) {
    const client = new IndividualProfileServicePromiseClient(baseUrl)
    enableDevTools(client)
    return client
  },
)
